import {type ReactNode, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {EmbeddedBlock, EmbeddedBlockVariant} from '@tinct/core-api-models';
import {HtmlBlockSelector} from '../../landing-page-editor/components/html-block-selector/html-block-selector';
import {type LandingPageUrls, selectLandingPageUrl} from '../../landing-page-editor/landing-page-editor';
import {usePreloadedPageHtml} from '../hooks/use-preloaded-page-html';
import {isBlockPersonalizationEnabled} from '../../../utils/is-block-personalization-enabled';

type LandingPagePanelProps = {
  /**
   * The version's packaged-artifact URLs by dimension. Both origins use the
   * same CDN-served captures now; NEW-origin documents are responsive, so
   * every key serves the same document.
   */
  landingPageUrls?: LandingPageUrls | null;
  /**
   * External loading state — e.g. the packaged artifact is still being
   * generated (`htmlKeys` null while packaging is in flight).
   */
  loading?: boolean;
  blocks?: EmbeddedBlock[];
  /**
   * Editor mode renders every block — disabled ones included — so they can be
   * toggled back on. Previewer mode (the default) only overlays enabled blocks.
   */
  includeDisabledBlocks?: boolean;
  blockVariants?: EmbeddedBlockVariant[];
  selectedBlockId?: string | null;
  onBlockSelect?: (blockId: string | null) => void;
  overlay?: ReactNode;
  errorState?: ReactNode;
  /**
   * Rendered inside the card, flush with its left edge, beside the page
   * content (the studio's pages panel). Must size itself in-flow.
   */
  sidePanel?: ReactNode;
  /** Forwarded to HtmlBlockSelector: `text` enables inline text edition. */
  interactionMode?: 'select' | 'text';
  onVariantEditCommit?: (blockVariantId: string, html: string) => Promise<string | null | undefined>;
  /** Forwarded to HtmlBlockSelector: makes the selected block's badge editable. */
  onBlockUpdate?: (block: EmbeddedBlock) => void;
};

const noop = () => undefined;

/**
 * The studio's single-viewport preview. The iframe fills the panel and the
 * document follows the panel width: crossing a dimension-key breakpoint swaps
 * in that key's capture (largest key ≤ the width). The key is therefore
 * width-driven here — a deliberate product choice over the preview-iframe
 * contract's fixed-width rule, which the canvas artboards do follow.
 */
export const LandingPagePanel = ({
  landingPageUrls,
  loading = false,
  blocks = [],
  includeDisabledBlocks = false,
  selectedBlockId = null,
  blockVariants = [],
  onBlockSelect,
  overlay,
  errorState,
  sidePanel,
  interactionMode,
  onVariantEditCommit,
  onBlockUpdate,
}: LandingPagePanelProps) => {
  // The personalization toggle is applied here, at preview time: variants are
  // generated for every block, so a toggle just re-filters what gets applied —
  // no regeneration involved.
  // Memoized (like the canvas panel) so the resize re-renders — every frame
  // while the pages panel animates open/closed — hand HtmlBlockSelector
  // stable arrays instead of rebuilding its overlay targets each time.
  const enabledBlocks = useMemo(
    () => (includeDisabledBlocks ? blocks : blocks.filter(isBlockPersonalizationEnabled)),
    [blocks, includeDisabledBlocks]
  );
  const enabledBlockVariants = useMemo(() => {
    const enabledBlockIds = new Set(enabledBlocks.map((block) => block.id));
    return blockVariants.filter(
      (variant) => variant.block?.id && enabledBlockIds.has(variant.block.id)
    );
  }, [blockVariants, enabledBlocks]);

  const { htmlByUrl, isLoading: isPreloading } =
    usePreloadedPageHtml(landingPageUrls);

  const isLoading = loading || isPreloading;

  // The panel width drives the breakpoint. Measured synchronously before the
  // first paint (the observer's first callback only lands at the next
  // rendering opportunity), then tracked on every resize.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    setContainerWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const width = entries[entries.length - 1]?.contentRect.width;
      if (width) setContainerWidth(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const landingPageUrl = useMemo(
    () => selectLandingPageUrl(landingPageUrls, containerWidth),
    [landingPageUrls, containerWidth]
  );
  const pageHtml = (landingPageUrl && htmlByUrl[landingPageUrl]) || '';

  // Never blank the preview while a re-package lands (rule 1): keep the last
  // fetched document on screen until the new one has arrived — the
  // selector's spinner covers the gap. Adjusted during render (React's
  // state-from-props pattern) rather than written to a ref, which the hooks
  // lint forbids.
  const [lastHtml, setLastHtml] = useState(pageHtml);
  if (pageHtml && pageHtml !== lastHtml) setLastHtml(pageHtml);
  const displayedHtml = pageHtml || lastHtml;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-2 pr-2 h-full">
      <div className="flex min-h-0 flex-1 overflow-hidden rounded-3xl border border-border bg-background shadow-lg">
        {sidePanel}
        <div ref={containerRef} className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
          {errorState ? (
            errorState
          ) : displayedHtml ? (
            // Keyed on the URL — which changes per breakpoint and carries
            // `packagedAt` — so the iframe remounts per document while the
            // measuring container above stays put.
            <div key={landingPageUrl ?? 'last'} className="h-full">
              <HtmlBlockSelector
                html={displayedHtml}
                isLoading={isLoading}
                blocks={enabledBlocks}
                blockVariants={enabledBlockVariants}
                selectedBlockId={selectedBlockId}
                onBlockSelect={onBlockSelect ?? noop}
                onBlockUpdate={onBlockUpdate}
                interactionMode={interactionMode}
                onVariantEditCommit={onVariantEditCommit}
              />
            </div>
          ) : isLoading ? (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              Loading page...
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              {landingPageUrls
                ? 'Failed to load page'
                : 'No landing page URL provided'}
            </div>
          )}
          {overlay}
        </div>
      </div>
    </div>
  );
};
